/**
 * PartDossierPanel — the right-hand "Details" tab for part-knowledge assets
 * (CONTRACT §3 GET /assembly/{asset}/part/{cad_id}).
 *
 * Identity (name, level, breadcrumb, children) renders from the manifest tree
 * immediately and never waits on the network; the dossier fills in around it.
 * Every part reference is a button that calls onSelect, so the tree and the
 * 3D view stay in sync with whatever is clicked here.
 *
 * Props:
 *   nodeId       selected cad id or null
 *   tree         buildTree() result
 *   parts        client from useParts()
 *   onSelect     (id) => void
 *   onClose      optional () => void
 *   onTrace      optional (faultId) => void — fault message rows start a trace
 *   taskRequest  optional { cadId, taskId, nonce } — expand + scroll to a task
 *   domain       optional asset domain (config/assets.js); 'industrial' reads
 *                the grouping code as "System V32" instead of "ATA 32"
 *   knowledgeNote optional provenance line for the knowledge pack, shown in the footer
 *   examplePart  optional part the empty state suggests opening
 */

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, ShieldAlert, Wrench, Link2, X } from 'lucide-react';
import { useDossier, REQUEST_TIMEOUT_MS } from './usePartKnowledge.js';
import { displayName } from '../assemblyTree.js';
import { connectionType, failureSeverity, statusFor } from '../diagnosticPalette.js';
import { codeLabelFor, faultListLabelFor } from '../../../config/assets.js';
import { SERVICE_KIT_LABELS } from '../../../data/industrial/dummyVacuumPumpData.js';
import './PartDossierPanel.css';

const R5_TEXT = 'Representative geometry — not OEM CAD';

const LEVEL_LABEL = {
  assembly: 'Assembly', subassembly: 'Sub-assembly', 'sub-assembly': 'Sub-assembly', unit: 'Unit',
};

const TASK_TYPE_LABEL = {
  inspection: 'Inspection', removal: 'Removal', installation: 'Installation', servicing: 'Servicing', test: 'Test',
};

const SEVERITY_RANK = { critical: 0, major: 1, minor: 2 };

const nameOf = (tree, id) => tree?.byId?.get(id)?.name ?? displayName(id);

function formatValue(value) {
  if (typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  }
  return value === null || value === undefined || value === '' ? '—' : String(value);
}

function PartLink({ id, tree, onSelect, className = '', children }) {
  const known = Boolean(tree?.byId?.has(id));
  return (
    <button
      type="button"
      className={`pkd-link ${className}`.trim()}
      onClick={() => known && onSelect?.(id)}
      disabled={!known}
      title={known ? `Select ${nameOf(tree, id)}` : `${id} is not in the loaded manifest`}
    >
      {children ?? nameOf(tree, id)}
    </button>
  );
}

/**
 * Summaries cite parts as "R Brake Assembly [U6_3_3_R_BRAKE_ASSEMBLY]". Known
 * ids become links; when the part's name directly precedes its id, the name
 * itself becomes the link so it is not printed twice.
 */
function LinkedText({ text, tree, onSelect }) {
  const chunks = String(text ?? '').split(/(\[[ASU]\d+(?:_[A-Z0-9]+)+\])/g);
  const out = [];
  chunks.forEach((chunk, i) => {
    const m = /^\[([ASU]\d+(?:_[A-Z0-9]+)+)\]$/.exec(chunk);
    if (!m || !tree?.byId?.has(m[1])) {
      out.push(chunk);
      return;
    }
    const name = nameOf(tree, m[1]);
    const prev = out.length ? out[out.length - 1] : null;
    if (typeof prev === 'string') {
      const trimmed = prev.replace(/\s+$/, '');
      if (trimmed.endsWith(name)) out[out.length - 1] = trimmed.slice(0, -name.length);
    }
    out.push(
      <PartLink key={`${i}-${m[1]}`} id={m[1]} tree={tree} onSelect={onSelect} className="pkd-ref">
        {name}
      </PartLink>,
    );
  });
  return out;
}

function Section({ title, count, action, children }) {
  return (
    <section className="pkd-section">
      <div className="pkd-section__head">
        <h4 className="pkd-section__title">
          {title}
          {typeof count === 'number' && <span className="pkd-section__count">{count}</span>}
        </h4>
        {action}
      </div>
      {children}
    </section>
  );
}

function SeverityTag({ severity }) {
  const s = failureSeverity(severity);
  return (
    <span className="pkd-sev">
      <span className="pkd-sev__icon" style={{ color: s.color }} aria-hidden="true">{s.icon}</span>
      {s.label}
    </span>
  );
}

function Likelihood({ value }) {
  if (!Number.isFinite(value)) return null;
  const v = Math.max(1, Math.min(5, Math.round(value)));
  return (
    <span className="pkd-likely" title={`Likelihood ${v} of 5`} aria-label={`Likelihood ${v} of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={`pkd-likely__pip${i <= v ? ' is-on' : ''}`} aria-hidden="true" />
      ))}
    </span>
  );
}

function TaskCard({ task, open, onToggle, tree, onSelect, showOwner, nodeId, cardRef }) {
  const bodyId = `pkd-task-${task.id}`;
  return (
    <li className={`pkd-task${open ? ' is-open' : ''}`} ref={cardRef}>
      <button
        type="button"
        className="pkd-task__head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <ChevronRight size={14} className="pkd-task__chev" aria-hidden="true" />
        <span className="pkd-task__title">{task.title}</span>
        <span className="pkd-task__meta">
          {TASK_TYPE_LABEL[task.type] ?? task.type}
          {task.interval ? ` · ${task.interval}` : ''}
          {task.service_kit && (
            <span className="pkd-kit" title={`Service kit ${task.service_kit}`}>
              {SERVICE_KIT_LABELS[task.service_kit] ?? task.service_kit}
            </span>
          )}
        </span>
      </button>
      {open && (
        <div className="pkd-task__body" id={bodyId}>
          <div className="pkd-task__ids">
            <code>{task.id}</code>
            {showOwner && task.cad_id !== nodeId && (
              <span className="pkd-owner">
                on <PartLink id={task.cad_id} tree={tree} onSelect={onSelect} />
              </span>
            )}
          </div>
          {task.safety.length > 0 && (
            <div className="pkd-safety" role="note" aria-label="Safety">
              <div className="pkd-safety__title">
                <ShieldAlert size={13} aria-hidden="true" /> Safety — before you start
              </div>
              <ul>
                {task.safety.map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>
          )}
          {task.access_parts.length > 0 && (
            <div className="pkd-task__row">
              <span className="pkd-task__label">Access first</span>
              <span className="pkd-task__chips">
                {task.access_parts.map((id) => (
                  <PartLink key={id} id={id} tree={tree} onSelect={onSelect} className="pkd-chip" />
                ))}
              </span>
            </div>
          )}
          {task.tools.length > 0 && (
            <div className="pkd-task__row">
              <span className="pkd-task__label"><Wrench size={11} aria-hidden="true" /> Tools</span>
              <span className="pkd-task__chips">
                {task.tools.map((tool) => <span key={tool} className="pkd-chip pkd-chip--static">{tool}</span>)}
              </span>
            </div>
          )}
          {task.steps.length > 0 && (
            <ol className="pkd-steps">
              {task.steps.map((step, i) => <li key={`${i}-${step}`}>{step}</li>)}
            </ol>
          )}
        </div>
      )}
    </li>
  );
}

function StateNote({ reason, onRetry }) {
  const copy = {
    not_found: ['○', 'No knowledge recorded for this part', 'The part exists in the geometry but the knowledge pack has no entry for it.', false],
    timeout: ['⧗', 'Knowledge service did not respond in time', `Stopped after ${Math.round(REQUEST_TIMEOUT_MS / 1000)} s so the panel stays responsive.`, true],
    unavailable: ['○', 'Knowledge service unavailable', 'The model, tree and overlays still work; this panel has nothing to add right now.', true],
  }[reason] ?? ['○', 'Knowledge service unavailable', '', true];
  return (
    <div className="pkd-state" role="status">
      <span className="pkd-state__icon" aria-hidden="true">{copy[0]}</span>
      <p className="pkd-state__title">{copy[1]}</p>
      {copy[2] && <p className="pkd-state__body">{copy[2]}</p>}
      {copy[3] && onRetry && (
        <button type="button" className="exview__btn" onClick={onRetry}>Try again</button>
      )}
    </div>
  );
}

function PartDossierPanelImpl({
  nodeId = null, tree, parts, onSelect, onClose, onTrace, taskRequest = null,
  domain = null, knowledgeNote = null, examplePart = 'the right engine',
}) {
  const [includeChildren, setIncludeChildren] = useState(false);
  const [openTasks, setOpenTasks] = useState(() => new Set());
  const taskRefs = useRef(new Map());
  const bodyRef = useRef(null);

  const { phase, data, reason, retry } = useDossier(parts, nodeId, includeChildren);
  const node = nodeId ? tree?.byId?.get(nodeId) : null;

  // New part: collapse tasks and scroll to top. Keep the sub-parts toggle.
  useEffect(() => {
    setOpenTasks(new Set());
    bodyRef.current?.scrollTo?.({ top: 0 });
  }, [nodeId]);

  // "Check task" from the trace panel: open it, pulling in sub-part tasks if needed.
  useEffect(() => {
    if (!taskRequest || taskRequest.cadId !== nodeId) return;
    setOpenTasks((prev) => new Set(prev).add(taskRequest.taskId));
  }, [taskRequest, nodeId]);

  useEffect(() => {
    if (!taskRequest || taskRequest.cadId !== nodeId || phase !== 'ready' || !data) return;
    const found = data.tasks.some((t) => t.id === taskRequest.taskId);
    if (!found && !includeChildren && (data.rollup?.tasks ?? 0) > data.tasks.length) {
      setIncludeChildren(true);
      return;
    }
    const el = taskRefs.current.get(taskRequest.taskId);
    el?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    el?.querySelector('button')?.focus?.({ preventScroll: true });
  }, [taskRequest, nodeId, phase, data, includeChildren]);

  const breadcrumb = useMemo(() => {
    if (!nodeId) return [];
    // Tree order is authoritative (root first); the dossier's list is a fallback
    // for ids the manifest does not know.
    const fromTree = tree?.ancestorsOf ? tree.ancestorsOf(nodeId).slice().reverse() : [];
    if (fromTree.length) return fromTree.map((id) => ({ cad_id: id, name: nameOf(tree, id) }));
    return data?.ancestors ?? [];
  }, [nodeId, tree, data]);

  const children = useMemo(() => {
    if (data?.children?.length) return data.children;
    return (tree?.childrenOf?.(nodeId) ?? []).map((id) => ({
      cad_id: id, name: nameOf(tree, id), level: tree.byId.get(id)?.level,
    }));
  }, [data, tree, nodeId]);

  const failureModes = useMemo(() => (data?.failure_modes ?? []).slice().sort((a, b) => (
    (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3)
    || (b.likelihood ?? 0) - (a.likelihood ?? 0)
  )), [data]);

  const connectionGroups = useMemo(() => {
    const groups = new Map();
    for (const c of data?.connections ?? []) {
      if (!groups.has(c.type)) groups.set(c.type, []);
      groups.get(c.type).push(c);
    }
    return [...groups.entries()];
  }, [data]);

  if (!nodeId) {
    return (
      <aside className="pkd" aria-label="Part dossier">
        <div className="pkd-state pkd-state--idle" role="status">
          <span className="pkd-state__icon" aria-hidden="true">◎</span>
          <p className="pkd-state__title">No part selected</p>
          <p className="pkd-state__body">
            Pick a part in the 3D view or the tree, or type &ldquo;open {examplePart}&rdquo;
            in the ask bar.
          </p>
        </div>
        <footer className="pkd-foot">
          {knowledgeNote && <span className="pkd-foot__pack">{knowledgeNote}</span>}
          <span>{R5_TEXT}</span>
        </footer>
      </aside>
    );
  }

  const ready = phase === 'ready' || (phase === 'loading' && data);
  const level = LEVEL_LABEL[data?.level ?? node?.level] ?? 'Part';
  const name = data?.name ?? node?.name ?? displayName(nodeId);
  const rollup = data?.rollup;
  const coverage = data?.coverage;
  const hasChildren = children.length > 0;
  const toggleTask = (id) => setOpenTasks((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <aside className="pkd" aria-label={`Part dossier: ${name}`} aria-busy={phase === 'loading'}>
      <header className="pkd-head">
        <div className="pkd-head__main">
          <span className="pkd-head__level">
            {level}
            {data?.ata && <span className="pkd-ata">{codeLabelFor(domain)} {data.ata}</span>}
          </span>
          <h3 className="pkd-head__name">{name}</h3>
          <code className="pkd-head__id">{nodeId}</code>
        </div>
        {onClose && (
          <button type="button" className="pkd-iconbtn" onClick={onClose} aria-label="Clear selection">
            <X size={15} aria-hidden="true" />
          </button>
        )}
      </header>

      {breadcrumb.length > 0 && (
        <nav className="pkd-crumbs" aria-label="Assembly path">
          {breadcrumb.map((a) => (
            <span key={a.cad_id} className="pkd-crumbs__step">
              <PartLink id={a.cad_id} tree={tree} onSelect={onSelect}>{a.name}</PartLink>
              <span className="pkd-crumbs__sep" aria-hidden="true">›</span>
            </span>
          ))}
          <span className="pkd-crumbs__self" aria-current="location">{name}</span>
        </nav>
      )}

      <div className="pkd-body" ref={bodyRef}>
        {phase === 'loading' && !data && (
          <div className="pkd-loading" role="status" aria-live="polite">
            <div className="pkd-skel pkd-skel--wide" />
            <div className="pkd-skel" />
            <div className="pkd-skel pkd-skel--short" />
            <span className="pkd-loading__label">Loading part knowledge…</span>
          </div>
        )}

        {phase === 'error' && <StateNote reason={reason} onRetry={retry} />}

        {ready && data && (
          <>
            {(data.summary || data.rollup_summary) && (
              <section className="pkd-section pkd-section--first">
                {data.summary && (
                  <p className="pkd-summary"><LinkedText text={data.summary} tree={tree} onSelect={onSelect} /></p>
                )}
                {data.rollup_summary && (
                  <p className="pkd-summary pkd-summary--rollup">
                    <span className="pkd-kicker">Across sub-parts</span>
                    <LinkedText text={data.rollup_summary} tree={tree} onSelect={onSelect} />
                  </p>
                )}
              </section>
            )}
            {!data.summary && !data.rollup_summary && (
              <p className="pkd-thin">No summary recorded for this part yet.</p>
            )}

            {rollup && hasChildren && (
              <dl className="pkd-rollup" aria-label="Rolled-up counts">
                <div><dt>Sub-parts</dt><dd>{rollup.descendants ?? 0}</dd></div>
                <div><dt>Failure modes</dt><dd>{rollup.failure_modes ?? 0}</dd></div>
                <div>
                  <dt>Critical</dt>
                  <dd>
                    {(rollup.critical ?? 0) > 0 && (
                      <span className="pkd-sev__icon" style={{ color: statusFor('critical').color }} aria-hidden="true">■ </span>
                    )}
                    {rollup.critical ?? 0}
                  </dd>
                </div>
                <div><dt>Tasks</dt><dd>{rollup.tasks ?? 0}</dd></div>
              </dl>
            )}

            {data.fault_messages.length > 0 && (
              <Section title={faultListLabelFor(domain)} count={data.fault_messages.length}>
                <ul className="pkd-list">
                  {data.fault_messages.map((m) => {
                    const s = statusFor(m.severity);
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          className="pkd-row pkd-row--button"
                          onClick={() => onTrace?.(m.id)}
                          disabled={!onTrace}
                          title={onTrace ? `Trace ${m.id}` : undefined}
                        >
                          <span className="pkd-sev__icon" style={{ color: s.color }} aria-hidden="true">{s.icon}</span>
                          <code className="pkd-row__code">{m.id}</code>
                          <span className="pkd-row__text">{m.title}</span>
                          <span className="pkd-row__meta">{s.label}{m.role ? ` · ${m.role}` : ''}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Section>
            )}

            {data.specs.length > 0 && (
              <Section title="Specifications" count={data.specs.length}>
                <table className="pkd-specs">
                  <tbody>
                    {data.specs.map((s, i) => (
                      <tr key={`${s.name}-${i}`}>
                        <th scope="row">
                          {s.name}
                          {s.note && <span className="pkd-specs__note">{s.note}</span>}
                        </th>
                        <td>
                          <span className="pkd-specs__value">{formatValue(s.value)}</span>
                          {s.unit && <span className="pkd-specs__unit">{s.unit}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}

            <Section
              title="Failure modes"
              count={failureModes.length}
              action={hasChildren ? (
                <button
                  type="button"
                  className={`pkd-toggle${includeChildren ? ' is-on' : ''}`}
                  aria-pressed={includeChildren}
                  onClick={() => setIncludeChildren((v) => !v)}
                >
                  <span className="pkd-toggle__track" aria-hidden="true"><span className="pkd-toggle__thumb" /></span>
                  Include sub-parts
                </button>
              ) : null}
            >
              {failureModes.length === 0 ? (
                <p className="pkd-thin">
                  {hasChildren && !includeChildren
                    ? 'None recorded on this part itself — include sub-parts to see theirs.'
                    : 'No failure modes recorded.'}
                </p>
              ) : (
                <ul className="pkd-fms">
                  {failureModes.map((f) => (
                    <li key={`${f.cad_id}-${f.id}`} className={`pkd-fm pkd-fm--${f.severity}`}>
                      <div className="pkd-fm__head">
                        <span className="pkd-fm__mode">{f.mode}</span>
                        <SeverityTag severity={f.severity} />
                      </div>
                      {f.cad_id !== nodeId && (
                        <div className="pkd-owner">
                          on <PartLink id={f.cad_id} tree={tree} onSelect={onSelect} />
                        </div>
                      )}
                      <dl className="pkd-fm__body">
                        {f.cause && <div><dt>Cause</dt><dd>{f.cause}</dd></div>}
                        {f.effect && <div><dt>Effect</dt><dd>{f.effect}</dd></div>}
                        {f.detection && <div><dt>Detection</dt><dd>{f.detection}</dd></div>}
                      </dl>
                      <div className="pkd-fm__foot">
                        <code>{f.id}</code>
                        {f.iso14224 && (
                          <code className="pkd-fm__iso" title="ISO 14224-style failure-mode code">{f.iso14224}</code>
                        )}
                        <Likelihood value={f.likelihood} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {data.tasks.length > 0 && (
              <Section title="Tasks" count={data.tasks.length}>
                <ul className="pkd-tasks">
                  {data.tasks.map((task) => (
                    <TaskCard
                      key={`${task.cad_id}-${task.id}`}
                      task={task}
                      open={openTasks.has(task.id)}
                      onToggle={() => toggleTask(task.id)}
                      tree={tree}
                      onSelect={onSelect}
                      showOwner={includeChildren}
                      nodeId={nodeId}
                      cardRef={(el) => {
                        if (el) taskRefs.current.set(task.id, el); else taskRefs.current.delete(task.id);
                      }}
                    />
                  ))}
                </ul>
              </Section>
            )}

            {connectionGroups.length > 0 && (
              <Section title="Connections" count={data.connections.length}>
                {connectionGroups.map(([type, list]) => {
                  const spec = connectionType(type);
                  return (
                    <div key={type} className="pkd-cx">
                      <div className="pkd-cx__type">
                        <span className="pkd-cx__swatch" style={{ background: spec.color }} aria-hidden="true" />
                        {spec.label}
                      </div>
                      <ul className="pkd-list">
                        {list.map((c) => (
                          <li key={c.id || `${c.direction}-${c.other.cad_id}`} className="pkd-row">
                            <span className="pkd-cx__dir" title={c.direction === 'in' ? 'Incoming' : 'Outgoing'}>
                              <Link2 size={11} aria-hidden="true" />
                              {c.direction === 'in' ? 'from' : 'to'}
                            </span>
                            <PartLink id={c.other.cad_id} tree={tree} onSelect={onSelect}>
                              {c.other.name ?? nameOf(tree, c.other.cad_id)}
                            </PartLink>
                            {c.label && <span className="pkd-row__meta">{c.label}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </Section>
            )}

            {hasChildren && (
              <Section title="Sub-parts" count={children.length}>
                <ul className="pkd-children">
                  {children.map((c) => (
                    <li key={c.cad_id}>
                      <PartLink id={c.cad_id} tree={tree} onSelect={onSelect} className="pkd-child">
                        <span>{c.name}</span>
                        <span className="pkd-child__level">{LEVEL_LABEL[c.level] ?? ''}</span>
                      </PartLink>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {coverage && (
              <Section title="Knowledge coverage">
                <div className="pkd-cov">
                  <div
                    className="pkd-cov__bar"
                    role="meter"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round((coverage.score ?? 0) * 100)}
                    aria-label="Knowledge coverage"
                  >
                    <span style={{ width: `${Math.round((coverage.score ?? 0) * 100)}%` }} />
                  </div>
                  <span className="pkd-cov__pct">{Math.round((coverage.score ?? 0) * 100)}%</span>
                </div>
                <p className="pkd-cov__counts">
                  {coverage.specs ?? 0} specs · {coverage.failure_modes ?? 0} failure modes · {coverage.tasks ?? 0} tasks
                </p>
              </Section>
            )}
          </>
        )}

        {!ready && phase !== 'loading' && hasChildren && phase === 'error' && (
          <Section title="Sub-parts" count={children.length}>
            <ul className="pkd-children">
              {children.map((c) => (
                <li key={c.cad_id}>
                  <PartLink id={c.cad_id} tree={tree} onSelect={onSelect} className="pkd-child">
                    <span>{c.name}</span>
                  </PartLink>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      <footer className="pkd-foot">
        {data?.provenance && <span className="pkd-foot__pack">{data.provenance}</span>}
        {knowledgeNote && <span className="pkd-foot__pack">{knowledgeNote}</span>}
        <span>{R5_TEXT}</span>
      </footer>
    </aside>
  );
}

export const PartDossierPanel = memo(PartDossierPanelImpl);
PartDossierPanel.displayName = 'PartDossierPanel';
export default PartDossierPanel;
